import { doc, getDoc, serverTimestamp, updateDoc, type DocumentData, type UpdateData } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { parseUserAgent } from '@/lib/user-agent';
import { logActivityEvent } from '@/lib/activity-events';

const THROTTLE_MS = 5 * 60 * 1000; // Only write lastActiveAt once every 5 minutes per session
const SESSION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // Keep session history for 30 days
const LAST_ACTIVE_KEY = 'lastActiveTrackedAt';
const SESSION_FLAG_KEY = 'activeSessionTracked';

/**
 * Records that the given user is currently active on the site.
 * - Always (throttled to once every 5 min) updates users/{uid}.lastActiveAt.
 * - Once per browser session (tab), also records the session start time
 *   (pruned to the last 30 days) and the parsed device/browser info.
 *
 * Never creates users/{uid}: for a brand new signup this runs before the
 * profile has been written, and creating the doc here would fire the
 * onNewUserCreate notification with no name/email on it.
 */
export function trackUserActivity(uid: string): void {
  if (typeof window === 'undefined') return;

  try {
    const lastTracked = Number(window.sessionStorage.getItem(LAST_ACTIVE_KEY) || 0);
    const isNewSession = !window.sessionStorage.getItem(SESSION_FLAG_KEY);

    if (!isNewSession && Date.now() - lastTracked < THROTTLE_MS) return;

    writeActivity(uid, isNewSession)
      .then((written) => {
        // Only start the throttle window once something was actually recorded,
        // so a skipped write (profile not created yet) is retried on the next call.
        if (!written) return;
        window.sessionStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
        window.sessionStorage.setItem(SESSION_FLAG_KEY, '1');
      })
      .catch((error) => {
        console.error('[trackUserActivity] Failed to update activity:', error);
      });
  } catch (error) {
    console.error('[trackUserActivity] Failed to update activity:', error);
  }
}

async function writeActivity(uid: string, isNewSession: boolean): Promise<boolean> {
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);

  // The profile is created by the signup flow. If it isn't there yet, skip this
  // round rather than creating a partial doc.
  if (!snap.exists()) return false;

  const updates: UpdateData<DocumentData> = { lastActiveAt: serverTimestamp() };

  if (isNewSession) {
    const device = parseUserAgent(window.navigator.userAgent);
    updates.lastDevice = device;

    const cutoff = Date.now() - SESSION_WINDOW_MS;
    const existing = (snap.data()?.recentSessionStarts as number[] | undefined) || [];
    updates.recentSessionStarts = [...existing.filter((ts) => ts > cutoff), Date.now()];

    logActivityEvent(uid, 'session_start', { device });
  }

  await updateDoc(userRef, updates);
  return true;
}
